import {
  FIXED_PRODUCT_IDS,
  FORM_IDS,
} from '@bulknutrients/bulk-shared-lib/constants';
import { isErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import type {
  CreateAccountFormFields,
  CustomForm,
  CustomFormInputWithOptions,
} from '@bulknutrients/bulk-shared-lib/interfaces/IForms';
import { getErrorMessage } from '@bulknutrients/bulk-shared-lib/utils';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { SuccessAlert } from '@components/Alerts/SuccessAlert';
import { Input } from '@components/FormComponents';
import { SkeletonText } from '@components/Placeholders/Skeleton';
import { Fieldset } from '@components/PortableText/Form';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useSignup } from '@hooks/customer/useSignup';
import { useKlaviyo } from '@hooks/useKlaviyo';
import { useRouter } from 'next/router';
import { type ReactElement, type ReactNode, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, ButtonType } from '../Button';
import {
  CreateAccountFields,
  KlaviyoErrorMessage,
  SignupErrorMessage,
} from './CreateAccountForm';

interface Props {
  requestSampleForm: CustomForm;
  sampleStock: Record<string, boolean>;
  source?: string;
}

// This page imports much of its content from CreateAccountForm.
// It is essentially an extenison of that form to include the request sample form fields and logic.
export function RequestSampleCreateAccountForm({
  requestSampleForm,
  sampleStock,
  source = 'standard',
}: Props): ReactElement {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>();
  const [submitting, setSubmitting] = useState(false);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    <p className="dark:text-white">
      Thanks! We've received your free sample request 🥳 Look out for an email
      from us so you can set your account password! You will shortly be
      redirected to the login page.{' '}
      <TextLink href="/login">Click here if you're not redirected.</TextLink>
    </p>
  );

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const signup = useSignup();
  const router = useRouter();
  const { freeSampleRequestedSubscribe, newsletterSubscribe, smsSubscribe } =
    useKlaviyo();

  const rhfMethods = useForm<CreateAccountFormFields>({
    mode: 'onTouched',
    defaultValues: {
      country: 'AU',
      requested_sample_source: source,
      addressAutocomplete: '',
    },
  });

  const recaptchaCheck = async () => {
    const recaptchaValue = recaptchaRef?.current?.getValue();

    if (!recaptchaValue) {
      return false;
    }

    const captchaCheckResponse = await fetch('/api/recaptcha', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ code: recaptchaValue as string, version: 'v2' }),
    });

    const captchaCheck = await captchaCheckResponse.json();

    if (isErrorResponse(captchaCheck)) {
      return false;
    }

    return captchaCheck.data;
  };

  const handleSignup = async (formData: CreateAccountFormFields) => {
    // scroll to the top because a spinner replaces form causing page height
    // to change and the user location so we want that to be obvious to them
    window.scrollTo(0, 0);
    setSubmitting(true);

    // Check the Captcha is correct
    const captchaCheck = await recaptchaCheck();
    if (!captchaCheck) {
      setError(true);
      setErrorMessage('Please complete the captcha correctly');
      setSubmitting(false);
      return;
    }

    const requestedSample = formData.requested_sample;

    const freeSampleStock = (await fetch(
      `/api/stock-level?id=${FIXED_PRODUCT_IDS.freeSample}`
    )
      .then((res) => res.json())
      .then((res) => {
        if (isErrorResponse(res)) {
          return {};
        }

        return res.data;
      })) as { [key: string]: boolean };

    // Check if the sample selected is in stock
    if (requestedSample && !freeSampleStock[requestedSample]) {
      setError(true);
      setErrorMessage(
        <Paragraph>
          Sorry, we don't have any of that sample in stock right now. Please
          select another sample or try again later.
        </Paragraph>
      );
      setSubmitting(false);
      recaptchaRef.current?.reset();
      return;
    }

    try {
      const placeholderPassword = `${Math.random().toString(36).substring(2, 15)}aA1`;

      const customer = await signup({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        password: placeholderPassword,
        addresses: [
          {
            first_name: formData.firstName,
            last_name: formData.lastName,
            address1: formData.address1,
            city: formData.suburb,
            state_or_province: formData.state,
            postal_code: formData.postcode,
            country_code: formData.country,
            address_type: 'residential',
          },
        ],
        createdFromFreeSample: true,
      });

      if (isErrorResponse(customer)) {
        throw Error(`signup${customer.error.message.join('. ')}`);
      }

      const klaviyoPromises = [] as Array<Promise<boolean>>;

      // If the customer has signed up for the newsletter then we can add them to Klaviyo
      if (formData.newsletter) {
        klaviyoPromises.push(
          newsletterSubscribe({
            email: formData.email,
            phone: formData.phone,
            firstName: formData.firstName,
            lastName: formData.lastName,
          })
        );
        klaviyoPromises.push(
          smsSubscribe({
            email: formData.email,
            phone: formData.phone,
            firstName: formData.firstName,
            lastName: formData.lastName,
          })
        );
      }

      const sampleName = (
        requestSampleForm.fieldsets[0].inputs[0] as CustomFormInputWithOptions
      ).options.find((o) => o.value == requestedSample)?.label;

      klaviyoPromises.push(
        freeSampleRequestedSubscribe({
          email: formData.email,
          freeSample: sampleName ?? requestedSample ?? '',
          source,
        })
      );

      const klaviyoSubscribeResponses = await Promise.all(klaviyoPromises);

      // even if klaviyo fails, continue on to free sample request. Handle klaviyo at the end.
      const response = await fetch('/api/form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formId: FORM_IDS.FREE_SAMPLE,
          formData,
        }),
      });

      if (!response.ok) {
        const { error } = await response.json();
        throw Error(error.message.join('. '));
      }

      if (klaviyoSubscribeResponses.some((response) => !response)) {
        setSuccessMessage(
          <div>
            <Paragraph>
              We've received your free sample request 🥳 Look out for an email
              from us so you can set your account password!{' '}
            </Paragraph>
            <Paragraph>
              Unfortunately, we failed to subscribe you to our newsletter or sms
              list. You can opt into emails and text messages from your{' '}
              <TextLink href="/account/settings">account settings</TextLink>, or
              reach out to our{' '}
              <TextLink href="/contact-us">customer service team</TextLink> to
              get you signed up.
            </Paragraph>
            <Paragraph>
              You will shortly be redirected to the login page.{' '}
              <TextLink href="/login">
                Click here if you're not redirected.
              </TextLink>
            </Paragraph>
          </div>
        );
      }

      rhfMethods.reset();
      setSubmitting(false);
      setSuccess(true);

      setTimeout(() => {
        router.push('/login');
      }, 5000);
    } catch (err) {
      const message = getErrorMessage(err);
      if (message.startsWith('signup')) {
        setErrorMessage(<SignupErrorMessage msg={message.slice(6)} />);
      } else {
        setErrorMessage(
          <div>
            <Paragraph>
              Sorry, something went wrong with the last step there (actually
              getting the free sample)! You have an account now, but please{' '}
              <TextLink href="/contact-us">
                reach out to our customer service team
              </TextLink>{' '}
              to finalise getting that sample!
            </Paragraph>
            <Paragraph>
              You can also tell them the error you saw was:{' '}
              <pre>{errorMessage}</pre>
            </Paragraph>
          </div>
        );
      }
      setError(true);
      setSubmitting(false);
      recaptchaRef.current?.reset();
      return;
    }
  };

  return (
    <>
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
      <SuccessAlert show={success} setShow={setSuccess}>
        {successMessage}
      </SuccessAlert>
      {submitting ? (
        <div className="mb-6 mt-4">
          <SkeletonText heightPixels={700} />
        </div>
      ) : (
        <div>
          {/* 
        Form provider exists at this level so that we can place additional form fields other than create
          account fields into the form
        */}
          <FormProvider {...rhfMethods}>
            <form
              onSubmit={rhfMethods.handleSubmit(handleSignup)}
              className="grid grid-cols-1 gap-5"
            >
              <h4>Your details</h4>

              <CreateAccountFields methods={rhfMethods} includeAddress={true} />

              {requestSampleForm.fieldsets.map((fieldset) => (
                <Fieldset
                  key={fieldset._key}
                  fieldset={fieldset}
                  showOtherInput={showOtherInput}
                  setShowOtherInput={setShowOtherInput}
                />
              ))}

              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
              />

              <Input
                type="checkbox"
                label="Send me exclusive news & offers"
                name="newsletter"
                value="newsletter"
                required
                description={
                  <>
                    I understand that by submitting this form, I consent to
                    receive email and sms communications from Bulk Nutrients and
                    agree to the{' '}
                    <TextLink
                      href="/bulk-nutrients-terms-conditions"
                      target="_blank"
                    >
                      T&Cs
                    </TextLink>{' '}
                    and{' '}
                    <TextLink
                      href="/bulk-nutrients-privacy-policy"
                      target="_blank"
                    >
                      Privacy Policy
                    </TextLink>
                    .
                  </>
                }
              />

              <div className="btn-create-account">
                <Button
                  type={ButtonType.submit}
                  disabled={submitting || !rhfMethods.formState.isValid}
                  buttonStyle="primary"
                >
                  Request Sample
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      )}
    </>
  );
}
